/**
 * ייבוא לקוחות — the intake verification screen.
 *
 * Three steps, and the middle one is the point: choose a file, **review every row and every
 * problem**, then confirm. Nothing is written until the office confirms, because a bad import
 * into a client list is expensive to unpick and a firm's spreadsheet always contains something
 * unexpected.
 *
 * Problems are shown per row and split by severity, because the two need different responses:
 * an error means the row will be skipped and the file needs fixing; a warning means the row
 * imports with a value dropped or defaulted, which the office may well accept.
 */
import { useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Button,
  type Column,
  DataTable,
  EmptyState,
  ErrorState,
  KpiStrip,
  Money,
  PageHeader,
  Panel,
} from "@onecpa/ui";
import { api, type ImportPreview, type ImportProblem, type ImportRow } from "../api/client.js";
import { useSession } from "../app/session.js";

const FREQUENCY_LABELS: Record<string, string> = {
  monthly: "חודשי",
  bi_monthly: "דו-חודשי",
  semi_annual: "חצי שנתי",
  annual: "שנתי",
  not_applicable: "ללא",
};

export function ClientImport() {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => api.clients.importPreview(session.orgId, file),
    onSuccess: (result) => {
      setPreview(result);
      setError(null);
    },
    onError: (mutationError: Error) => {
      setPreview(null);
      setError(mutationError.message);
    },
  });

  const commit = useMutation({
    mutationFn: (rows: ImportRow[]) => api.clients.importCommit(session.orgId, rows),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      const skippedForLimit = result.skipped.filter((row) => row.reason === "plan_limit").length;
      session.toast(
        skippedForLimit > 0
          ? `נוספו ${result.created} לקוחות · ${skippedForLimit} לא נוספו בגלל מגבלת התוכנית`
          : `נוספו ${result.created} לקוחות`,
        skippedForLimit > 0 ? "warning" : "success",
      );
      await navigate({ to: "/clients" });
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  /** Problems grouped by row, so the table can show them beside the row they belong to. */
  const problemsByRow = new Map<number, ImportProblem[]>();
  for (const problem of preview?.problems ?? []) {
    const list = problemsByRow.get(problem.rowNumber) ?? [];
    list.push(problem);
    problemsByRow.set(problem.rowNumber, list);
  }

  const blocked = new Set(preview?.blockedRowNumbers ?? []);
  const errorCount = (preview?.problems ?? []).filter((row) => row.severity === "error").length;
  const warningCount = (preview?.problems ?? []).filter((row) => row.severity === "warning").length;

  const columns: Column<ImportRow>[] = [
    {
      key: "row",
      header: "שורה",
      width: "70px",
      sticky: true,
      cell: (row) => <bdi className="tnum text-xs text-ink-faint">{row.rowNumber}</bdi>,
    },
    {
      key: "name",
      header: "שם לקוח",
      cell: (row) => <span className="text-sm font-semibold text-ink">{row.name}</span>,
    },
    {
      key: "type",
      header: "סוג",
      cell: (row) => (
        <span className="text-xs text-ink-muted">
          {row.clientType === "company" ? "חברה בע״מ" : "עוסק מורשה"}
        </span>
      ),
    },
    {
      key: "contact",
      header: "פרטי קשר",
      hideOnSmall: true,
      cell: (row) => (
        <span className="flex flex-col text-2xs text-ink-muted">
          {row.email && <bdi dir="ltr">{row.email}</bdi>}
          {row.phoneE164 && (
            <bdi className="tnum" dir="ltr">
              {row.phoneE164}
            </bdi>
          )}
          {!row.email && !row.phoneE164 && <span className="text-ink-faint">—</span>}
        </span>
      ),
    },
    {
      key: "fee",
      header: "ריטיינר",
      align: "end",
      cell: (row) =>
        row.monthlyFeeAgorot !== undefined ? (
          <Money agorot={row.monthlyFeeAgorot} colorize={false} />
        ) : (
          <span className="text-xs text-ink-faint">—</span>
        ),
    },
    {
      key: "cadence",
      header: "תדירויות",
      hideOnSmall: true,
      cell: (row) => (
        <span className="flex flex-wrap gap-1">
          {row.frequencies
            .filter((frequency) => frequency.frequency !== "not_applicable")
            .map((frequency) => (
              <Badge key={frequency.authority} tone="neutral">
                {FREQUENCY_LABELS[frequency.frequency] ?? frequency.frequency}
              </Badge>
            ))}
        </span>
      ),
    },
    {
      key: "problems",
      header: "הערות",
      align: "end",
      cell: (row) => {
        const problems = problemsByRow.get(row.rowNumber) ?? [];
        // A plain success chip rather than a status from another vocabulary: "בוצעה" belongs
        // to tasks, and borrowing it here would mean two different things wear the same label.
        if (problems.length === 0) return <Badge tone="success">תקין</Badge>;
        return (
          <span className="flex flex-col items-end gap-0.5">
            {problems.map((problem, index) => (
              <span
                key={index}
                className={`text-2xs ${problem.severity === "error" ? "font-semibold text-red-ink" : "text-ochre"}`}
              >
                {problem.message}
              </span>
            ))}
          </span>
        );
      },
    },
  ];

  // Blocked rows never reach the table — the parser did not produce a client for them — so
  // they are listed separately with their row number and reason.
  const blockedProblems = (preview?.problems ?? []).filter((problem) =>
    blocked.has(problem.rowNumber),
  );

  return (
    <>
      <PageHeader
        title="ייבוא לקוחות מאקסל"
        subtitle="שלב 1: הורדת תבנית · שלב 2: בדיקת השורות · שלב 3: אישור"
        action={
          <div className="flex items-center gap-2">
            <Button onClick={() => void api.clients.importTemplate(session.orgId)} icon="file-text">
              הורדת תבנית
            </Button>
            <Button onClick={() => void api.clients.importExport(session.orgId)}>
              ייצוא הלקוחות הקיימים
            </Button>
          </div>
        }
      />

      <div className="flex flex-col gap-4 p-5">
        <Panel title="בחירת קובץ">
          <div className="flex flex-col gap-3 px-3.5 py-3">
            <p className="text-xs text-ink-muted">
              יש להשתמש בתבנית שהורדה מהמערכת, או בייצוא של הלקוחות הקיימים. הכותרות בעברית והמערכת
              מזהה גם ניסוחים חלופיים כמו «דו חודשי» ללא מקף.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                aria-label="קובץ אקסל לייבוא"
                className="rounded-card border border-rule-strong bg-surface px-2.5 py-1.5 text-xs text-ink file:me-3 file:rounded-card file:border-0 file:bg-wash file:px-2.5 file:py-1 file:text-xs file:text-ink"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  setFileName(file.name);
                  upload.mutate(file);
                }}
              />
              {upload.isPending && (
                <span className="text-xs text-ink-faint">קורא את הקובץ…</span>
              )}
              {fileName && !upload.isPending && (
                <span className="text-xs text-ink-muted">
                  <bdi>{fileName}</bdi>
                </span>
              )}
            </div>

            {error && (
              <p role="alert" className="text-xs font-semibold text-red-ink">
                {error}
              </p>
            )}
          </div>
        </Panel>

        {preview && (
          <>
            <KpiStrip
              items={[
                { label: "שורות לייבוא", value: preview.importableCount, tone: "ledger" },
                {
                  label: "שורות שיידלגו",
                  value: preview.blockedRowNumbers.length,
                  tone: preview.blockedRowNumbers.length > 0 ? "red" : "ink",
                },
                { label: "אזהרות", value: warningCount, tone: warningCount > 0 ? "ochre" : "ink" },
                { label: "שגיאות", value: errorCount, tone: errorCount > 0 ? "red" : "ink" },
                {
                  label: "מקום בתוכנית",
                  value: preview.capacity.remaining,
                  tone: preview.importableCount > preview.capacity.remaining ? "red" : "ink",
                },
              ]}
            />

            {preview.capacityWarning && (
              <p className="rounded-card border border-warning-ink/30 bg-warning-bg px-3.5 py-2.5 text-xs text-warning-ink">
                {preview.capacityWarning}
              </p>
            )}

            {blockedProblems.length > 0 && (
              <Panel title={`שורות שיידלגו (${preview.blockedRowNumbers.length})`}>
                <ul className="divide-y divide-line-soft">
                  {blockedProblems.map((problem, index) => (
                    <li key={index} className="flex items-center gap-3 px-3.5 py-2">
                      <bdi className="tnum w-14 text-xs font-semibold text-ink">
                        שורה {problem.rowNumber}
                      </bdi>
                      <span className="flex-1 text-xs text-red-ink">{problem.message}</span>
                      {problem.column && <Badge tone="neutral">{problem.column}</Badge>}
                    </li>
                  ))}
                </ul>
                <p className="border-t border-line-soft px-3.5 py-2 text-2xs text-ink-faint">
                  מספרי השורות תואמים לקובץ שלכם, כולל שורות ריקות — ניתן לתקן ולהעלות שוב.
                </p>
              </Panel>
            )}

            <DataTable
              columns={columns}
              rows={preview.rows}
              rowKey={(row) => String(row.rowNumber)}
              density="compact"
              caption="השורות שייובאו"
              footnote="אדום = השורה תידלג · חרדל = השורה תיובא עם ערך שהושלם או הושמט"
              empty={
                <EmptyState
                  title="אין שורות לייבוא"
                  description="כל השורות בקובץ נחסמו. יש לתקן את הבעיות שלמעלה ולהעלות שוב."
                />
              }
            />

            {preview.importableCount > 0 && (
              <div className="flex items-center gap-3">
                <Button
                  variant="primary"
                  loading={commit.isPending}
                  onClick={() => commit.mutate(preview.rows)}
                >
                  אישור וייבוא {preview.importableCount} לקוחות
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setPreview(null);
                    setFileName(null);
                    if (fileInput.current) fileInput.current.value = "";
                  }}
                >
                  ביטול
                </Button>
                <span className="text-2xs text-ink-faint">עד לאישור לא נוצר דבר במערכת.</span>
              </div>
            )}
          </>
        )}

        {!preview && !upload.isPending && !error && (
          <Panel>
            <EmptyState
              title="טרם נבחר קובץ"
              description="הורידו את התבנית, מלאו אותה, והעלו כאן. השורות יוצגו לבדיקה לפני שמשהו נשמר."
              icon="file-text"
            />
          </Panel>
        )}

        {error && !preview && <ErrorState title="לא ניתן לקרוא את הקובץ" message={error} />}
      </div>
    </>
  );
}
